import { createMemo } from 'solid-js'
import type { MeasurementEntry, MetricDefinition } from '@fasting-timer/shared'

import { useI18n } from '../../i18n'
import {
   getEntriesForMetric,
   liveMeasurementEntries,
} from '../../state/measurementsStore'
import {
   measurementsChartValue,
   measurementsEffortBucket,
   measurementsGroup,
   measurementsRangeFilter,
   setMeasurementsGroup,
   setMeasurementsRangeFilter,
} from '../../state/measurementsUiStore'
import { targetMonthlyRunEffort } from '../../state/runEffortTargetStore'
import { getRangeTimestamps } from '../../utils/timeRange'
import GroupByChips from './GroupByChips'
import MeasurementList from './MeasurementList'
import MetricEntryForm from './MetricEntryForm'
import MetricSummaryCard from './MetricSummaryCard'
import RangeFilter from './RangeFilter'
import MetricCharts from './MetricCharts'
import {
   buildTimelinePoints,
   buildVolumeBuckets,
   extendRangeToEffortMonths,
   findBestEntry,
   getBucketSize,
   getEntryValue,
   getGroupOptions,
   prorateMonthlyTarget,
   selectEntries,
} from './metricViewUtils'
import { getEntryTimestampMs } from '../../state/measurementsStore'

type MetricViewProps = {
   definition: MetricDefinition
}

/**
 * Builds the effort bars and their target for metrics that derive an effort
 * score (run). Monthly effort always spans the last months, whatever the
 * history range, so the chart can show as many as fit its width.
 *
 * @param definition - The metric definition.
 * @param metricEntries - Every entry of the metric.
 * @param range - The selected history range.
 * @returns The effort buckets and the per-bucket target.
 */
function createEffortChartData(
   definition: () => MetricDefinition,
   metricEntries: () => MeasurementEntry[],
   range: () => { rangeStart: number; rangeEnd: number },
) {
   const effortRange = createMemo(() =>
      measurementsEffortBucket() === 'month' ?
         extendRangeToEffortMonths(range())
      :  range(),
   )
   const effortBuckets = createMemo(() =>
      'effort' in definition().derived ?
         buildVolumeBuckets(
            selectEntries(metricEntries(), definition(), effortRange(), null),
            entry => getEntryValue(entry, definition(), 'effort'),
            effortRange(),
            measurementsEffortBucket(),
         )
      :  [],
   )
   const effortTarget = createMemo(() =>
      prorateMonthlyTarget(targetMonthlyRunEffort(), measurementsEffortBucket()),
   )
   return { effortBuckets, effortTarget }
}

/**
 * Renders a registry metric: summary card, entry form, range and distance chips,
 * pace timeline, volume bars (plus effort bars with the monthly target for runs),
 * and the entry list.
 *
 * @param props - The metric definition.
 * @returns The view element.
 */
const MetricView = (props: MetricViewProps) => {
   const t = useI18n()

   const metricEntries = createMemo(() => {
      liveMeasurementEntries()
      return getEntriesForMetric(props.definition.id)
   })
   const range = createMemo(() =>
      getRangeTimestamps(
         measurementsRangeFilter(),
         metricEntries().map(getEntryTimestampMs),
      ),
   )
   const groupOptions = createMemo(() =>
      getGroupOptions(metricEntries(), props.definition),
   )
   /** Entries in range for the active group, oldest first (drives line, list, summary). */
   const groupedEntries = createMemo(() =>
      selectEntries(metricEntries(), props.definition, range(), measurementsGroup()),
   )
   /** Entries in range for every group, oldest first (drives volume bars). */
   const rangeEntries = createMemo(() =>
      selectEntries(metricEntries(), props.definition, range(), null),
   )
   /** The derived value the timeline plots; falls back to the primary when the metric lacks it. */
   const chartValueKey = createMemo(() =>
      measurementsChartValue() in props.definition.derived ?
         measurementsChartValue()
      :  props.definition.primary,
   )
   const points = createMemo(() =>
      buildTimelinePoints(groupedEntries(), props.definition, chartValueKey()),
   )
   const bucketSize = createMemo(() => getBucketSize(measurementsRangeFilter()))
   const buckets = createMemo(() => {
      const field = props.definition.barField
      return field ?
            buildVolumeBuckets(
               rangeEntries(),
               entry => entry.values[field] ?? null,
               range(),
               bucketSize(),
            )
         :  []
   })
   const { effortBuckets, effortTarget } = createEffortChartData(
      () => props.definition,
      metricEntries,
      range,
   )
   const bestEntry = createMemo(() => findBestEntry(groupedEntries(), props.definition))
   const latestEntry = createMemo(
      () => groupedEntries()[groupedEntries().length - 1] ?? null,
   )
   const descendingEntries = createMemo(() => [...groupedEntries()].reverse())

   return (
      <div id="metric-view" class="space-y-4">
         <MetricSummaryCard
            definition={props.definition}
            best={bestEntry()}
            latest={latestEntry()}
            group={measurementsGroup()}
         />
         <section class="lg:h-full pb-4 space-y-3 bg-transparent lg:p-4 lg:bg-surface-raised/70 lg:rounded-xl lg:border lg:border-edge-soft">
            <MetricEntryForm definition={props.definition} />
         </section>
         <section class="lg:h-full pb-4 space-y-4 bg-transparent lg:p-4 lg:bg-surface-raised/70 lg:rounded-xl lg:border lg:border-edge-soft">
            <h2 class="text-xl font-semibold text-ink">{t('History')}</h2>
            <RangeFilter
               id="measurement-filter-container"
               value={measurementsRangeFilter()}
               onChange={setMeasurementsRangeFilter}
            />
            <GroupByChips
               options={groupOptions()}
               value={measurementsGroup()}
               onChange={setMeasurementsGroup}
            />
            <MetricCharts
               points={points()}
               range={range()}
               valueKey={chartValueKey()}
               buckets={buckets()}
               bucketSize={bucketSize()}
               effortBuckets={effortBuckets()}
               effortBucketSize={measurementsEffortBucket()}
               effortTarget={effortTarget()}
            />
            <MeasurementList
               definition={props.definition}
               entries={descendingEntries()}
            />
         </section>
      </div>
   )
}

export default MetricView
